// Centralized Product Types for Consistent Interfaces
// This file provides standardized product interfaces to prevent field mismatches

/**
 * Raw Shopify Product from database - matches actual DB schema
 */
export interface ShopifyProductDB {
  id: string;
  shopify_product_id: string;
  user_id: string;
  shop_domain: string;
  title: string;
  description: string | null;
  handle: string | null;
  vendor: string | null;
  product_type: string | null;
  tags: string[] | null;
  status: 'active' | 'archived' | 'draft';
  published_at: string | null;
  variants: any;
  images: Array<{ url: string; altText?: string }>;
  options: any;
  min_price: number | string | null;
  max_price: number | string | null;
  total_inventory?: number;
  created_at: string;
  updated_at: string;
}

/**
 * Standard Product interface for UI components
 * Transforms DB data into consistent format for all components
 */
export interface StandardProduct {
  id: string;
  shopify_product_id: string;
  title: string;
  description: string | null;
  handle: string | null;
  vendor: string | null;
  product_type: string | null;
  tags: string[] | null;
  status: 'active' | 'archived' | 'draft';

  // Standardized image handling
  imageUrl: string | null;
  images: Array<{ url: string; altText?: string }>;

  // Standardized pricing
  price_range: string;
  min_price_number: number;
  max_price_number: number;

  // Additional fields
  inventory_quantity?: number;
}

/**
 * Compact Product interface for modals, dropdowns, and space-constrained components
 */
export interface CompactProduct {
  id: string;
  title: string;
  imageUrl: string | null;
  price_range: string;
  status: 'active' | 'archived' | 'draft';
}

/**
 * Product Card interface for full product displays
 */
export interface ProductCard {
  id: string;
  shopify_product_id: string;
  title: string;
  description?: string | null;
  status: 'active' | 'archived' | 'draft';
  images?: Array<{ url: string; altText?: string }>;
  min_price?: number | null;
  max_price?: number | null;
  vendor?: string | null;
  product_type?: string | null;
  tags?: string[] | null;
}

/**
 * Transform DB product to StandardProduct
 */
export function transformToStandardProduct(dbProduct: ShopifyProductDB): StandardProduct {
  const minPrice = parsePrice(dbProduct.min_price);
  const maxPrice = parsePrice(dbProduct.max_price);
  const price_range = formatPriceRange(dbProduct.min_price, dbProduct.max_price);

  return {
    id: dbProduct.id,
    shopify_product_id: dbProduct.shopify_product_id,
    title: dbProduct.title,
    description: dbProduct.description,
    handle: dbProduct.handle,
    vendor: dbProduct.vendor,
    product_type: dbProduct.product_type,
    tags: dbProduct.tags,
    status: dbProduct.status,
    imageUrl: dbProduct.images?.length > 0 ? dbProduct.images[0]?.url : null,
    images: dbProduct.images || [],
    price_range,
    min_price_number: minPrice,
    max_price_number: maxPrice,
    inventory_quantity: dbProduct.total_inventory,
  };
}

/**
 * Transform StandardProduct to CompactProduct
 */
export function transformToCompactProduct(product: StandardProduct): CompactProduct {
  return {
    id: product.id,
    title: product.title,
    imageUrl: product.imageUrl,
    price_range: product.price_range,
    status: product.status,
  };
}

// PRICE UTILITIES - Handle PostgreSQL numeric → JSON string → TypeScript number
export function parsePrice(priceString: number | string | null): number {
  if (priceString === null || priceString === undefined) return 0;
  if (typeof priceString === 'number') return priceString;
  const parsed = parseFloat(priceString);
  return isNaN(parsed) ? 0 : parsed;
}

export function formatPrice(price: number | string | null, currency = 'USD'): string {
  const numericPrice = typeof price === 'string' ? parsePrice(price) : price || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(numericPrice);
}

export function formatPriceRange(
  minPrice: number | string | null,
  maxPrice: number | string | null,
  currency = 'USD',
): string {
  const min = parsePrice(minPrice);
  const max = parsePrice(maxPrice);

  if (min === max) {
    return formatPrice(min, currency);
  }
  return `${formatPrice(min, currency)} - ${formatPrice(max, currency)}`;
}

/**
 * Transform DB product to ProductCard format (current ProductCard component interface)
 */
export function transformToProductCard(dbProduct: ShopifyProductDB): ProductCard {
  return {
    id: dbProduct.id,
    shopify_product_id: dbProduct.shopify_product_id,
    title: dbProduct.title,
    description: dbProduct.description,
    status: dbProduct.status,
    images: dbProduct.images,
    min_price: parsePrice(dbProduct.min_price),
    max_price: parsePrice(dbProduct.max_price),
    vendor: dbProduct.vendor,
    product_type: dbProduct.product_type,
    tags: dbProduct.tags,
  };
}
